// Types pour le formulaire de commande VISINOT

// === TYPES DE BASE ===
export type Formula = 'standard';
export type PropertyType = 'maison' | 'appartement' | 'immeuble' | 'terrain' | 'autre';
export type ProfessionalType = 'notaire' | 'agence' | 'autre';
export type Occupation = 'locataire' | 'proprietaire' | 'vide';
export type SaleType = 'gre_a_gre' | 'biddit' | 'vente_publique' | 'location' | 'autre';
export type AccessMode = 'cles_etude' | 'code' | 'cles_ailleurs';
export type DayOfWeek = 'lundi' | 'mardi' | 'mercredi' | 'jeudi' | 'vendredi' | 'samedi' | 'dimanche';
export type ControleType = 'peb' | 'elec';
export type MarketingUpgrade = 'video' | '360';
export type MarketingType = 'bien' | 'terrain';

// Legacy (compatibilité)
export type PanneauType = 'premiere_visite' | 'deplacement_dedie';

// === CRÉNEAUX RÉCURRENTS ===
export interface SlotCreneaux {
  jour: DayOfWeek;
  heureDebut: string; // Format "HH:mm"
}

// === HELPER ===
export const isBidditOrPublicSale = (saleType: SaleType): boolean => {
  return saleType === 'biddit' || saleType === 'vente_publique';
};

export const isPropertyOccupied = (occupation: Occupation): boolean => {
  return occupation !== 'vide';
};

// === INTERFACE ÉTAPE ===
export interface Step {
  id: string;
  title: string;
  description: string;
}

// === DONNÉES DU FORMULAIRE ===
export interface OrderFormData {
  // --- Packs principaux ---
  packVisites: boolean;
  packMarketing: boolean;
  marketingType?: MarketingType;
  marketingUpgrade?: MarketingUpgrade;
  marketingDrone?: boolean;
  packControles: ControleType[];

  // --- Legacy services (compatibilité email) ---
  formula: Formula;
  optionVisite: boolean;
  optionVisiteDimanche: boolean;
  optionPanneau: boolean;
  panneauType: PanneauType;
  panneauDate: string;
  optionOuvertureForcee: boolean;
  optionVideMaison: boolean;
  optionNettoyage: boolean;
  descriptionMission: string;

  // --- Donneur d'ordre ---
  societe: string;
  typeProfessionnel: ProfessionalType;
  nomPrenom: string;
  email: string;
  telephone: string;
  referenceInterne: string;
  adresseDonneurRue: string;
  adresseDonneurCP: string;
  adresseDonneurVille: string;
  tvaNumber: string;
  adresseFacturation: string;

  // --- Bien immobilier ---
  typeBien: PropertyType;
  adresseBien: string;
  lienAnnonce: string;
  occupation: Occupation;
  etage: string;
  ascenseur: boolean;
  particularites: string;

  // --- Contexte de vente ---
  typeVente: SaleType;
  deadline: string;
  documentsConsignes: string;

  // --- Contact occupant ---
  occupantNom: string;
  occupantTelephone: string;
  occupantEmail: string;
  disponibilitesGenerales: string;

  // --- Accès / Clés ---
  modeAcces: AccessMode;
  adresseRetraitCles: string;
  contactRetraitClesNom: string;
  contactRetraitClesTelephone: string;
  codeAcces: string;
  parkingAccesContraintes: string;

  // --- Créneaux (Biddit/Vente publique) ---
  slotCreneaux: SlotCreneaux[];
  slotDateDebut: string;

  // --- Consentements ---
  confirmProfessionnel: boolean;
  acceptConditions: boolean;
}

// === VALEURS INITIALES ===
export const initialOrderData: OrderFormData = {
  // Packs
  packVisites: false,
  packMarketing: false,
  marketingType: undefined,
  marketingUpgrade: undefined,
  marketingDrone: false,
  packControles: [],

  // Legacy
  formula: 'standard',
  optionVisite: false,
  optionVisiteDimanche: false,
  optionPanneau: false,
  panneauType: 'premiere_visite',
  panneauDate: '',
  optionOuvertureForcee: false,
  optionVideMaison: false,
  optionNettoyage: false,
  descriptionMission: '',

  // Donneur
  societe: '',
  typeProfessionnel: 'notaire',
  nomPrenom: '',
  email: '',
  telephone: '',
  referenceInterne: '',
  adresseDonneurRue: '',
  adresseDonneurCP: '',
  adresseDonneurVille: '',
  tvaNumber: '',
  adresseFacturation: '',

  // Bien
  typeBien: 'maison',
  adresseBien: '',
  lienAnnonce: '',
  occupation: 'vide',
  etage: '',
  ascenseur: false,
  particularites: '',

  // Contexte
  typeVente: 'gre_a_gre',
  deadline: '',
  documentsConsignes: '',

  // Contact
  occupantNom: '',
  occupantTelephone: '',
  occupantEmail: '',
  disponibilitesGenerales: '',

  // Accès
  modeAcces: 'cles_etude',
  adresseRetraitCles: '',
  contactRetraitClesNom: '',
  contactRetraitClesTelephone: '',
  codeAcces: '',
  parkingAccesContraintes: '',

  // Créneaux
  slotCreneaux: [
    { jour: 'mercredi', heureDebut: '17:00' },
    { jour: 'samedi', heureDebut: '10:00' },
  ],
  slotDateDebut: '',

  // Consentements
  confirmProfessionnel: false,
  acceptConditions: false,
};

// === ÉTAPES DYNAMIQUES ===
const BASE_STEPS: Step[] = [
  { id: 'services', title: 'Services', description: 'Choisissez vos services' },
  { id: 'contexte', title: 'Contexte', description: 'Type de vente' },
  { id: 'donneur', title: 'Donneur', description: 'Vos coordonnées' },
  { id: 'bien', title: 'Bien', description: 'Détails du bien' },
  { id: 'contact', title: 'Contact', description: 'Occupant / Contact' },
  { id: 'acces', title: 'Accès', description: 'Clés & contraintes' },
  { id: 'recap', title: 'Récapitulatif', description: 'Vérification & envoi' },
];

export const getSteps = (occupation: Occupation, packVisites: boolean = false): Step[] => {
  let steps = [...BASE_STEPS];

  // Étape contexte uniquement si Pack Visites sélectionné
  if (!packVisites) {
    steps = steps.filter(step => step.id !== 'contexte');
  }

  // Étape contact masquée si bien vide
  if (occupation === 'vide') {
    steps = steps.filter(step => step.id !== 'contact');
  }

  return steps;
};

// === PRICING ===
export interface PricingItem {
  label: string;
  price: number;
  isQuote?: boolean;
}

export interface OrderPricing {
  items: PricingItem[];
  subtotal: number;
  supplementVisites: number;
  extraVisitesCount: number;
  discount: number;
  discountPercent: number;
  hasComboDiscount: boolean;
  total: number;
  hasQuoteItems: boolean;
}

export interface OrderResponse {
  success: boolean;
  orderId?: string;
  message: string;
  error?: string;
}
